import { useState, useRef, useEffect } from 'react';
import { Paperclip, FileText, Image, Download, Loader2, AlertCircle, X, Pill, Activity, ExternalLink, Check, Copy, ShieldCheck } from 'lucide-react';
import { filesApi } from '@/api/http-client';

export interface ChatMessage {
  id: string;
  participantId: string;
  content: string;
  timestamp: string;
  attachment?: {
    fileUrl: string;
    fileName: string;
    fileSize?: number;
    mimeType?: string;
  };
}

interface SessionChatProps {
  messages: ChatMessage[];
  localParticipantId: string;
  onSend: (content: string, attachment?: { fileUrl: string; fileName: string; fileSize?: number; mimeType?: string }) => void;
  disabled?: boolean;
  userId?: string;
}

export function SessionChat({ messages, localParticipantId, onSend, disabled, userId }: SessionChatProps) {
  const [text, setText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [copiedPinId, setCopiedPinId] = useState<string | null>(null);
  const [pendingAttachment, setPendingAttachment] = useState<{
    fileUrl: string;
    fileName: string;
    fileSize?: number;
    mimeType?: string;
  } | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pendingAttachment]);

  const handleSend = () => {
    const trimmed = text.trim();
    if ((!trimmed && !pendingAttachment) || disabled || isUploading) return;

    onSend(trimmed || (pendingAttachment ? `Arquivo anexado: ${pendingAttachment.fileName}` : ''), pendingAttachment || undefined);
    setText('');
    setPendingAttachment(null);
    setUploadError(null);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Limite de 20MB para documentos e exames
    if (file.size > 20 * 1024 * 1024) {
      setUploadError('O arquivo selecionado é maior que 20MB.');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const ownerId = userId || localParticipantId || 'session-user';
      const uploaded = await filesApi.uploadFile(
        file,
        ownerId,
        'PATIENT',
        'MEDICAL_EXAM',
        'PRIVATE'
      );

      setPendingAttachment({
        fileUrl: uploaded.url || '',
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      });
    } catch (err) {
      console.error('[SessionChat] File upload error:', err);
      // Fallback gracioso com data URL para garantir visualização do exame na chamada
      try {
        const reader = new FileReader();
        reader.onload = () => {
          setPendingAttachment({
            fileUrl: reader.result as string,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
          });
          setIsUploading(false);
        };
        reader.readAsDataURL(file);
        return;
      } catch {
        setUploadError('Não foi possível anexar o arquivo. Tente novamente.');
      }
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-xl overflow-hidden shadow-xs">
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50/80 flex items-center justify-between">
        <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider">Chat & Documentos</h3>
        <span className="text-[10px] text-muted-foreground flex items-center gap-1 font-medium">
          <Paperclip className="w-3 h-3 text-primary" />
          Envio de exames e PDFs
        </span>
      </div>

      {/* Área de mensagens */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {messages.length === 0 ? (
          <div className="text-xs text-gray-400 text-center py-6 space-y-1">
            <p className="font-semibold text-gray-500">Nenhuma mensagem ainda.</p>
            <p className="text-[11px]">Você pode enviar mensagens ou anexar fotos de exames e laudos diretamente pelo botão de anexo abaixo.</p>
          </div>
        ) : (
          messages.map((m) => {
            const isLocal = m.participantId === localParticipantId;
            return (
              <div
                key={m.id}
                className={`flex ${isLocal ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-xs sm:text-sm shadow-xs ${
                    isLocal ? 'bg-blue-600 text-white rounded-br-xs' : 'bg-gray-100 text-gray-900 rounded-bl-xs'
                  }`}
                >
                  <div className="text-[10px] opacity-75 mb-1 font-semibold flex items-center justify-between gap-2">
                    <span>{isLocal ? 'Você' : 'Médico / Paciente'}</span>
                    <span>{m.timestamp ? new Date(m.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  </div>

                  {m.content && <div className="break-words leading-relaxed">{m.content}</div>}

                  {/* Card Oficial de Documento Médico (Receita, Atestado, Exames via MeMed ICP-Brasil) */}
                  {m.attachment && (m.attachment.mimeType?.includes('pdf') || m.content?.includes('MeMed')) ? (
                    <div className="mt-2.5 pt-2 border-t border-white/20">
                      <div className={`p-3 rounded-xl border flex flex-col gap-2.5 ${
                        isLocal
                          ? 'bg-blue-700/60 border-blue-500/40 text-white'
                          : 'bg-white border-neutral-200 text-neutral-900 shadow-sm'
                      }`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded-lg ${isLocal ? 'bg-blue-600/80 text-white' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'}`}>
                              {m.content?.includes('Atestado') ? (
                                <FileText className="w-4 h-4" />
                              ) : m.content?.includes('Exame') ? (
                                <Activity className="w-4 h-4" />
                              ) : (
                                <Pill className="w-4 h-4" />
                              )}
                            </div>
                            <span className="font-bold text-xs truncate" title={m.attachment.fileName}>
                              {m.attachment.fileName.replace('.pdf', '')}
                            </span>
                          </div>
                          <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full shrink-0 flex items-center gap-1 ${
                            isLocal
                              ? 'bg-blue-500/30 text-blue-100 border border-blue-400/40'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-300'
                          }`}>
                            <ShieldCheck className="w-3 h-3" />
                            ICP-Brasil
                          </span>
                        </div>

                        {/* Extrai e exibe o PIN caso exista no texto */}
                        {(() => {
                          const pinMatch = m.content.match(/(?:PIN|desbloqueio):\s*\*?([A-Za-z0-9]+)/i);
                          const pin = pinMatch ? pinMatch[1] : null;
                          if (!pin) return null;

                          const isCopied = copiedPinId === m.id;
                          return (
                            <div className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold ${
                              isLocal ? 'bg-blue-800/60 text-blue-100 border border-blue-600/40' : 'bg-neutral-50 text-neutral-800 border border-neutral-200'
                            }`}>
                              <span>Código de Acesso (PIN): <strong className="font-mono text-sm tracking-wide">{pin}</strong></span>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(pin);
                                  setCopiedPinId(m.id);
                                  setTimeout(() => setCopiedPinId(null), 2000);
                                }}
                                className="flex items-center gap-1 text-[11px] font-bold text-primary hover:underline cursor-pointer ml-2"
                              >
                                {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                                <span>{isCopied ? 'Copiado!' : 'Copiar'}</span>
                              </button>
                            </div>
                          );
                        })()}

                        <a
                          href={m.attachment.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-bold transition-all shadow-xs ${
                            isLocal
                              ? 'bg-white text-blue-700 hover:bg-blue-50'
                              : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          }`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span>Abrir e Baixar Documento Oficial (PDF)</span>
                        </a>
                      </div>
                    </div>
                  ) : m.attachment ? (
                    <div className="mt-2 pt-2 border-t border-white/20">
                      <a
                        href={m.attachment.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 p-2 rounded-lg text-xs font-semibold transition-colors ${
                          isLocal
                            ? 'bg-blue-700/80 hover:bg-blue-800 text-white'
                            : 'bg-white hover:bg-gray-50 text-blue-700 border border-gray-200 shadow-xs'
                        }`}
                      >
                        {m.attachment.mimeType?.includes('image') ? (
                          <Image className="w-4 h-4 shrink-0" />
                        ) : (
                          <FileText className="w-4 h-4 shrink-0" />
                        )}
                        <span className="truncate flex-1" title={m.attachment.fileName}>
                          {m.attachment.fileName}
                        </span>
                        <Download className="w-3.5 h-3.5 shrink-0" />
                      </a>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pré-visualização do Anexo Pendente */}
      {pendingAttachment && (
        <div className="px-3 py-2 bg-blue-50 border-t border-blue-100 flex items-center justify-between text-xs text-blue-900">
          <div className="flex items-center gap-1.5 truncate">
            <FileText className="w-4 h-4 text-blue-600 shrink-0" />
            <span className="truncate font-semibold">{pendingAttachment.fileName}</span>
            <span className="text-[10px] text-blue-600">
              ({pendingAttachment.fileSize ? `${Math.round(pendingAttachment.fileSize / 1024)} KB` : 'anexo'})
            </span>
          </div>
          <button
            type="button"
            onClick={() => setPendingAttachment(null)}
            className="text-blue-700 hover:text-red-600 p-1"
            title="Remover anexo"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Erro de upload */}
      {uploadError && (
        <div className="px-3 py-1.5 bg-red-50 text-red-700 text-[11px] flex items-center gap-1 border-t border-red-100">
          <AlertCircle className="w-3 h-3 shrink-0" />
          <span>{uploadError}</span>
        </div>
      )}

      {/* Barra de envio e anexos */}
      <div className="p-2 sm:p-2.5 border-t border-gray-200 flex items-center gap-1.5 bg-gray-50/50">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
          className="hidden"
          disabled={isUploading}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          title="Anexar exame, laudo ou documento (PDF, imagem)"
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer disabled:opacity-50 shrink-0"
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
          ) : (
            <Paperclip className="w-4 h-4" />
          )}
        </button>

        <input
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Digite ou anexe um exame..."
          className="flex-1 px-3 py-2 text-base sm:text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
        />

        <button
          type="button"
          onClick={handleSend}
          disabled={(!text.trim() && !pendingAttachment) || isUploading}
          className="px-3.5 py-2 bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 cursor-pointer shadow-xs shrink-0"
        >
          Enviar
        </button>
      </div>
    </div>
  );
}
